// eslint-disable-next-line @typescript-eslint/ban-ts-comment -- Icon and Popover are still JS
// @ts-nocheck — remove once Icon and Popover are converted to TypeScript
import type { HTMLAttributes, ReactNode } from "react";
import { Fragment, useId } from "react";
import styled from "styled-components";
import { themeGet } from "@styled-system/theme-get";
import shouldForwardProp from "@styled-system/should-forward-prop";
import { compose, layout, space } from "styled-system";
import type { LayoutProps, SpaceProps } from "styled-system";

import Icon from "../Icon";
import Popover from "../Popover";

/**
 * Segmented control for switching views — only one item is selected at a
 * time. Built on native radios sharing `name`, so arrow keys, Tab and screen
 * reader semantics come from the browser; React owns which one is checked.
 */

type ButtonGroupItemBase<TValue extends string> = {
  value: TValue;
  label: ReactNode;
  /** Radio id. Defaults to `${name}-${value}`. */
  id?: string;
  disabled?: boolean;
  /** Shown in a Popover on hover/focus and announced as the radio's description. */
  tooltipText?: string;
};

export type ButtonGroupIconItem<TValue extends string = string> =
  ButtonGroupItemBase<TValue> & {
    /** Kebab-case icon name, rendered in the regular (`far`) style. */
    iconName: string;
  };

export type ButtonGroupTextItem<TValue extends string = string> =
  ButtonGroupItemBase<TValue> & { iconName?: never };

export type ButtonGroupItemConfig<TValue extends string = string> =
  | ButtonGroupIconItem<TValue>
  | ButtonGroupTextItem<TValue>;

/** Icons are all-or-none: every item sets `iconName`, or none do. */
export type ButtonGroupItems<TValue extends string = string> =
  | readonly ButtonGroupIconItem<TValue>[]
  | readonly ButtonGroupTextItem<TValue>[];

export type ButtonGroupLabelPosition = "inline" | "top";

export type ButtonGroupProps<TValue extends string = string> = Omit<
  HTMLAttributes<HTMLDivElement>,
  "onChange" | "children"
> &
  SpaceProps &
  LayoutProps & {
    /** Radio group name; also the default id prefix for each item. */
    name: string;
    items: ButtonGroupItems<TValue>;
    /** Value of the selected item. */
    value: TValue | null | undefined;
    onChange: (value: NoInfer<TValue>) => void;
    /** Visible group label, e.g. "View by:". Names the group for assistive tech. */
    label?: ReactNode;
    /** `inline` sits the label inside the track; `top` sits it above. */
    labelPosition?: ButtonGroupLabelPosition;
  };

const Wrapper = styled("div").withConfig({
  shouldForwardProp: (prop) => shouldForwardProp(prop),
})<SpaceProps & LayoutProps>`
  display: inline-flex;
  flex-direction: column;
  gap: ${themeGet("space.xs")};
  width: fit-content;
  ${compose(space, layout)}
`;

const Track = styled.div`
  display: inline-flex;
  align-items: stretch;
  gap: ${themeGet("space.xs")};
  box-sizing: border-box;
  height: ${themeGet("appScale.inputHeightDefault")};
  padding: ${themeGet("space.xs")};
  border: ${themeGet("borderWidths.1")} solid
    ${themeGet("colors.surfaceRaisedBorder")};
  border-radius: ${themeGet("radii.2")};
  background: ${themeGet("colors.buttonGroupTrack")};
`;

const InlineLabel = styled.span`
  align-self: center;
  padding: 0 ${themeGet("space.s")};
  color: ${themeGet("colors.subText")};
  font-size: ${themeGet("fontSizes.1")};
  font-weight: ${themeGet("fontWeights.1")};
  white-space: nowrap;
`;

const TopLabel = styled.span`
  color: ${themeGet("colors.subTextBody")};
  font-size: ${themeGet("fontSizes.1")};
  font-weight: ${themeGet("fontWeights.2")};
`;

const Radio = styled.input.attrs({ type: "radio" })`
  position: absolute;
  width: 0;
  height: 0;
  margin: 0;
  opacity: 0;
  pointer-events: none;

  &:checked + [data-button-group-segment] {
    cursor: default;
    color: ${themeGet("colors.buttonGroupSelectedText")};
    font-weight: ${themeGet("fontWeights.2")};
    background: ${themeGet("colors.buttonGroupSelected")};
    border-color: ${themeGet("colors.surfaceRaisedBorder")};
  }

  &:focus-visible + [data-button-group-segment] {
    border-color: ${themeGet("colors.focusOutline")};
    box-shadow: ${themeGet("shadows.thickOutline")}
      ${themeGet("colors.primary30")};
  }

  &:disabled + [data-button-group-segment] {
    cursor: not-allowed;
    color: ${themeGet("colors.disabledText")};
    border-color: transparent;
  }
`;

const Segment = styled.label`
  display: flex;
  align-items: center;
  gap: ${themeGet("space.s")};
  box-sizing: border-box;
  height: 100%;
  line-height: 1;
  padding: 0 ${themeGet("space.s")};
  border: ${themeGet("borderWidths.1")} solid transparent;
  border-radius: ${themeGet("radii.2")};
  color: ${themeGet("colors.subTextBody")};
  font-size: ${themeGet("fontSizes.1")};
  font-weight: ${themeGet("fontWeights.2")};
  white-space: nowrap;
  cursor: pointer;
  transition: ${themeGet("transition.transitionDefault")};

  &:hover {
    border-color: ${themeGet("colors.focusOutline")};
  }
`;

const ButtonGroup = <TValue extends string = string>({
  name,
  items,
  value,
  onChange,
  label,
  labelPosition = "inline",
  "aria-label": ariaLabel,
  ...props
}: ButtonGroupProps<TValue>) => {
  const labelId = `${useId()}-label`;
  const itemList: readonly ButtonGroupItemConfig<TValue>[] = items;
  // JS callers bypass the all-or-none type, so a partial set shows no icons.
  const showIcons = itemList.every((item) => Boolean(item.iconName));

  return (
    <Wrapper {...props}>
      {label && labelPosition === "top" && (
        <TopLabel id={labelId}>{label}</TopLabel>
      )}
      <Track
        role="radiogroup"
        aria-labelledby={label ? labelId : undefined}
        aria-label={ariaLabel}
      >
        {label && labelPosition === "inline" && (
          <InlineLabel id={labelId}>{label}</InlineLabel>
        )}
        {itemList.map((item) => {
          const inputId = item.id ?? `${name}-${item.value}`;
          const descriptionId = item.tooltipText
            ? `${inputId}-description`
            : undefined;
          const segment = (
            <>
              <Radio
                id={inputId}
                name={name}
                value={item.value}
                checked={item.value === value}
                disabled={item.disabled}
                aria-describedby={descriptionId}
                onChange={() => onChange(item.value)}
              />
              <Segment htmlFor={inputId} data-button-group-segment>
                {showIcons && item.iconName && (
                  <Icon icon={["far", item.iconName]} aria-hidden="true" />
                )}
                {item.label}
              </Segment>
            </>
          );

          if (!item.tooltipText) {
            return <Fragment key={item.value}>{segment}</Fragment>;
          }

          // The radio stays the only tab stop; focusing it still opens the Popover.
          return (
            <Popover
              key={item.value}
              text={item.tooltipText}
              direction="top"
              width="fit-content"
              tabIndex={-1}
            >
              <>
                {segment}
                <span id={descriptionId} hidden>
                  {item.tooltipText}
                </span>
              </>
            </Popover>
          );
        })}
      </Track>
    </Wrapper>
  );
};

export default ButtonGroup;
